import { useState, useCallback } from "react";
import {
  useAllSchemeIssues,
  useReconDetails,
  useToggleSchemeRecon,
} from "http-hooks/schIssue/useSchemeIssue";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingRow,
  EmptyRow,
} from "components/ui/Table";
import Select from "components/ui/Select";
import SelectVendors from "components/ui/selectors/SelectVendors";
import SelectCustomers from "components/ui/selectors/SelectCustomers";
import Modal from "components/ui/modals";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import authHeader from "services/auth-header";
import { EyeIcon } from "@heroicons/react/24/outline";
import Input from "components/ui/Input";
import { formatDateAndTime } from "utils/date";

const filterTypeOptions = [
  { value: "N", label: "Non-Reconciled" },
  { value: "A", label: "All Orders" },
];

const useSchemesForVendor = (vendorId) => {
  return useQuery({
    queryKey: ["schemes", vendorId],
    queryFn: async () => {
      if (!vendorId) return [];
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/schememaster?noPagination=true&vendor=${vendorId}`,
        { headers: authHeader() }
      );
      return response.data.data.data || [];
    },
    enabled: Boolean(vendorId),
  });
};

export default function SchemeOrderRecon() {
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [filterType, setFilterType] = useState(filterTypeOptions[0]);
  const [fetchOrders, setFetchOrders] = useState(false);
  const [selectedBatchIds, setSelectedBatchIds] = useState([]);
  const [fetchDetails, setFetchDetails] = useState(false);
  const [issueModalData, setIssueModalData] = useState(null);

  const { data: schemes = [], isLoading: isSchemesLoading } =
    useSchemesForVendor(selectedVendor?.ps_Id);

  const orderFilters = {
    type: "O",
    ...(filterType?.value === "N" ? { recon: "0" } : {}),
    ...(selectedScheme ? { schmId: selectedScheme.value } : {}),
    ...(selectedCustomer ? { custid: selectedCustomer.ps_Id } : {}),
    noPagination: true,
  };

  const canFetch = Boolean(selectedVendor) && Boolean(selectedScheme);

  const {
    data: { data: orders } = {},
    isLoading: ordersLoading,
    isFetching: ordersFetching,
  } = useAllSchemeIssues(orderFilters, fetchOrders && canFetch);

  const {
    data: reconDetails,
    isLoading: detailsLoading,
    isFetching: detailsFetching,
  } = useReconDetails(
    { batch_ids: selectedBatchIds.join(",") },
    fetchDetails && selectedBatchIds.length > 0
  );

  const { mutateAsync: mutateToggle, isPending: isToggling } =
    useToggleSchemeRecon();

  const orderBatches = (() => {
    if (!orders || orders.length === 0) return [];
    const batchMap = {};
    for (const o of orders) {
      const key = o.batch_id || `single_${o.issd_vchno}`;
      if (!batchMap[key]) {
        batchMap[key] = {
          batch_id: o.batch_id,
          first_vchno: o.issd_vchno,
          schdate: o.schdate,
          schdocno: o.schdocno,
          customerName: o.customerDetail?.name || "-",
          recon: o.recon,
          itemCount: 0,
        };
      }
      batchMap[key].itemCount++;
    }
    return Object.values(batchMap);
  })();

  const orderOptions = orderBatches.map((b) => ({
    value: b.batch_id,
    label: `${b.schdocno} - ${formatDateAndTime(b.schdate)} (${b.itemCount} items)`,
  }));

  const selectedOrderOptions = orderOptions.filter((opt) =>
    selectedBatchIds.includes(opt.value)
  );

  const handleUpdate = useCallback(() => {
    setFetchOrders(true);
    setFetchDetails(false);
    setSelectedBatchIds([]);
  }, []);

  const handleOrderSelect = useCallback((selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setSelectedBatchIds([]);
      setFetchDetails(false);
      return;
    }
    const ids = selectedOptions.map((opt) =>
      typeof opt === "object" ? opt.value : opt
    );
    setSelectedBatchIds(ids);
    setFetchDetails(true);
  }, []);

  const handleToggleRecon = async (row) => {
    const newRecon = row.recon === 1 ? 0 : 1;
    try {
      await mutateToggle({
        batch_id: row.batch_id || undefined,
        issd_vchno: !row.batch_id ? row.issd_vchno : undefined,
        recon: newRecon,
      });
    } catch (error) {
      console.error("Toggle recon failed:", error);
    }
  };

  const schemeOptions = schemes.map((s) => ({
    value: s.schemeid,
    label: `${s.sch_name} - ${formatDateAndTime(s.sch_date)}`,
  }));

  const formatNumber = (num, decimals = 3) => {
    return Number(num || 0).toLocaleString("en-IN", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      {/* Issue Details Modal */}
      {issueModalData && (
        <Modal
          open={!!issueModalData}
          setOpen={(val) => !val && setIssueModalData(null)}
          maxWidth="2xl"
        >
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Issue Details
            </h3>
            <div className="text-sm text-gray-500 mb-4 space-y-1">
              <p>
                Order Doc: <span className="font-medium text-gray-700">{issueModalData.schdocno}</span>
                {" | "}Gift Item: <span className="font-medium text-gray-700">{issueModalData.giftItemName}</span>
              </p>
              <p>
                Ordered: <span className="font-medium text-gray-700">{formatNumber(issueModalData.ordQty)}</span>
                {" | "}Total Issued: <span className="font-medium text-gray-700">{formatNumber(issueModalData.issuedQty)}</span>
                {" | "}Pending:{" "}
                <span
                  className={`font-medium ${
                    issueModalData.ordQty - issueModalData.issuedQty <= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {formatNumber(issueModalData.ordQty - issueModalData.issuedQty)}
                </span>
              </p>
            </div>

            {issueModalData.issues && issueModalData.issues.length > 0 ? (
              <div className="overflow-y-auto" style={{ maxHeight: "350px" }}>
                <Table>
                  <THead>
                    <TR hover={false}>
                      <TH first compact>Issue Doc No</TH>
                      <TH compact>Date</TH>
                      <TH compact className="text-right" last>Qty</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {issueModalData.issues.map((issue) => (
                      <TR key={issue.issd_vchno}>
                        <TD first compact>{issue.schdocno}</TD>
                        <TD compact>{formatDateAndTime(issue.schdate)}</TD>
                        <TD compact className="text-right font-semibold" last>
                          {formatNumber(issue.itmqty)}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-6">
                No issues recorded against this order item.
              </p>
            )}

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setIssueModalData(null)}
                className="rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold leading-6 text-gray-900">
            Scheme Order Reconciliation
          </h1>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <SelectVendors
          label="Vendor *"
          value={selectedVendor}
          onChange={(val) => {
            setSelectedVendor(val);
            setSelectedScheme(null);
            setFetchOrders(false);
            setSelectedBatchIds([]);
          }}
          showAddButton={false}
        />

        <Select
          label="Scheme *"
          value={selectedScheme}
          onChange={(opt) => {
            setSelectedScheme(opt);
            setFetchOrders(false);
            setSelectedBatchIds([]);
          }}
          options={schemeOptions}
          placeholder="Select Scheme"
          isSearchable={true}
          isClearable={true}
          isLoading={isSchemesLoading}
          isDisabled={!selectedVendor}
        />

        <SelectCustomers
          label="Customer"
          value={selectedCustomer}
          onChange={(val) => {
            setSelectedCustomer(val);
            setFetchOrders(false);
            setSelectedBatchIds([]);
          }}
          isClearable={true}
          showAddButton={false}
        />

        <Select
          label="Filter"
          options={filterTypeOptions}
          value={filterType}
          onChange={(opt) => setFilterType(opt || filterTypeOptions[0])}
          isClearable={false}
        />
      </div>

      <div className="mb-6">
        <button
          type="button"
          onClick={handleUpdate}
          disabled={!canFetch || ordersFetching}
          className="rounded-md bg-gray-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {ordersFetching ? "Loading..." : "Load Orders"}
        </button>
      </div>

      {/* Main content: split panel */}
      <div
        className="grid grid-cols-1 lg:grid-cols-12 gap-4 border border-gray-200 rounded-lg overflow-hidden"
        style={{ minHeight: "400px", maxHeight: "70vh" }}
      >
        {/* Left panel - Order list */}
        <div className="lg:col-span-4 p-3 border-r border-gray-200 overflow-y-auto">
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            Scheme Orders
          </label>
          {ordersLoading ? (
            <p className="text-sm text-gray-500">Loading orders...</p>
          ) : (
            <Select
              options={orderOptions}
              value={selectedOrderOptions}
              onChange={handleOrderSelect}
              isMulti={true}
              placeholder="Choose orders"
              isSearchable={true}
              isClearable={true}
              showLabel={false}
            />
          )}

          {orderBatches.length > 0 && (
            <p className="mt-2 text-xs text-gray-400">
              {orderBatches.length} order(s) found
            </p>
          )}
        </div>

        {/* Right panel - Order items vs issues */}
        <div className="lg:col-span-8 overflow-auto">
          <Table>
            <THead>
              <TR hover={false}>
                <TH first compact>Doc No</TH>
                <TH compact>Gift Item</TH>
                <TH compact className="text-right">Ord Qty</TH>
                <TH compact className="text-right">Issued Qty</TH>
                <TH compact className="text-right">Pending</TH>
                <TH compact className="text-center">Issues</TH>
                <TH compact className="text-center w-16" last>
                  Recon
                </TH>
              </TR>
            </THead>
            <TBody>
              {detailsLoading || detailsFetching ? (
                <LoadingRow colSpan={7} />
              ) : !reconDetails || reconDetails.length === 0 ? (
                <EmptyRow
                  colSpan={7}
                  message={
                    selectedBatchIds.length === 0
                      ? "Select orders to view details"
                      : "No details found"
                  }
                />
              ) : (
                reconDetails.map((row) => {
                  const pending = row.ordQty - row.issuedQty;
                  const isFulfilled = pending <= 0;
                  return (
                    <TR key={row.issd_vchno}>
                      <TD first compact>{row.schdocno}</TD>
                      <TD compact>{row.giftItemName}</TD>
                      <TD compact className="text-right">
                        {formatNumber(row.ordQty)}
                      </TD>
                      <TD compact className="text-right font-semibold">
                        {formatNumber(row.issuedQty)}
                      </TD>
                      <TD
                        compact
                        className={`text-right font-semibold ${
                          isFulfilled ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {formatNumber(pending)}
                      </TD>
                      <TD compact className="text-center">
                        <button
                          type="button"
                          onClick={() => setIssueModalData(row)}
                          className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-800 text-xs"
                          title="View issue details"
                        >
                          <EyeIcon className="h-4 w-4" />
                          <span>{row.issues?.length || 0}</span>
                        </button>
                      </TD>
                      <TD compact className="text-center" last>
                        <Input
                          type="checkbox"
                          showLabel={false}
                          value={row.recon === 1}
                          isDisabled={isToggling}
                          onChange={() => handleToggleRecon(row)}
                        />
                      </TD>
                    </TR>
                  );
                })
              )}
            </TBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
